import { tns, TinySliderInstance } from 'tiny-slider';

export default class ProductSlider {
    static init(): void {
        const sliders: NodeListOf<HTMLElement> = document.querySelectorAll('.js-product-slider');

        sliders.forEach((slider) => {
            // data-slider-min-width: pod touto šířkou slider nevzniká (např. služby jsou
            // na telefonu jen seznam), při změně šířky se zakládá / ruší
            const minWidth = Number(slider.dataset.sliderMinWidth);

            if (!minWidth) {
                this.create(slider);
                return;
            }

            const query = window.matchMedia(`(min-width: ${minWidth}px)`);
            let instance: TinySliderInstance | null = null;
            // tns() nad už jednou zrušeným kontejnerem nic nezaloží, obnovuje se přes rebuild()
            let destroyed: TinySliderInstance | null = null;

            const update = (): void => {
                if (query.matches && !instance) {
                    instance = destroyed ? destroyed.rebuild() : this.create(slider);
                    if (destroyed) {
                        this.bindWheel(slider, instance);
                        this.bindDragBounds(slider);
                    }
                } else if (!query.matches && instance) {
                    instance.destroy();
                    destroyed = instance;
                    instance = null;
                }
            };

            update();
            query.addEventListener('change', update);
        });
    }

    static create(slider: HTMLElement): TinySliderInstance {
        // Šířka slidu z data-slide-width (výchozí 340 px), pod lg (1024 px) případně
        // z data-slide-width-mobile; na úzkých displejích ji omezujeme šířkou rodiče,
        // aby se slide vešel celý
        const slideWidth = Number(slider.dataset.slideWidth) || 340;
        const mobileSlideWidth = Number(slider.dataset.slideWidthMobile) || slideWidth;
        const availableWidth = slider.parentElement?.clientWidth || slideWidth;

        const instance = tns({
            container: slider,
            fixedWidth: Math.min(mobileSlideWidth, availableWidth),
            responsive: {
                1024: {
                    fixedWidth: Math.min(slideWidth, availableWidth),
                },
            },
            gutter: 28,
            edgePadding: 0,
            controls: false,
            nav: false,
            loop: false,
            mouseDrag: true,
            swipeAngle: false,
        });

        this.bindWheel(slider, instance);
        this.bindDragBounds(slider);

        return instance;
    }

    // Tiny-slider při tažení posouvá kontejner bez omezení, takže za krajními
    // slidy vzniká velká prázdná plocha. Přetažení za okraj proto tlumíme
    // (rubber-band) a omezujeme na MAX_OVERSCROLL px.
    static bindDragBounds(slider: HTMLElement): void {
        const MAX_OVERSCROLL = 60;
        const viewport = slider.closest('.tns-ovh') as HTMLElement | null;

        if (!viewport) {
            return;
        }

        const dampen = (overshoot: number): number =>
            MAX_OVERSCROLL * (1 - 1 / ((overshoot * 0.5) / MAX_OVERSCROLL + 1));

        let lastWritten = '';

        const observer = new MutationObserver(() => {
            if (slider.style.transform === lastWritten) {
                return;
            }

            const match = slider.style.transform.match(/-?\d+(\.\d+)?(?=px)/);

            if (!match) {
                return;
            }

            const x = parseFloat(match[0]);
            const min = Math.min(0, viewport.clientWidth - slider.offsetWidth);
            let clamped = x;

            if (x > 0) {
                clamped = dampen(x);
            } else if (x < min) {
                clamped = min - dampen(min - x);
            }

            if (Math.abs(clamped - x) > 0.5) {
                slider.style.transform = slider.style.transform.replace(match[0], clamped.toFixed(2));
                lastWritten = slider.style.transform;
            }
        });

        observer.observe(slider, { attributes: true, attributeFilter: ['style'] });
    }

    static bindWheel(slider: HTMLElement, instance: TinySliderInstance): void {
        const outer = (slider.closest('.tns-outer') ?? slider) as HTMLElement;

        let isThrottled = false;

        outer.addEventListener(
            'wheel',
            (event: WheelEvent) => {
                if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
                    return;
                }

                event.preventDefault();

                if (isThrottled) {
                    return;
                }

                isThrottled = true;
                instance.goTo(event.deltaX > 0 ? 'next' : 'prev');

                window.setTimeout(() => {
                    isThrottled = false;
                }, 400);
            },
            { passive: false },
        );
    }
}
